import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import AuditingParameterAddEditModal from "pages/AllCommonModal/AuditingParameterAddEditModal/auditing-parameter-add-edit-modal";
import { submitAuditingParameterFilterData } from "model/AuditingParameterModel";
import Spinners from "components/Common/Spinner";
import { useAuditingParameterContext } from "libs/context/AuditingParameterContext";
import CommonTable from "components/Common/TableContainer";
import moment from "moment";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";


const AuditingParameter = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchAuditingParameterContextData, auditingParameterContextData } =
    useAuditingParameterContext() || {};

  const [auditingParameterAddEditModalOpen, setAuditingParameterAddEditModalOpen] =
    useState(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemId, setItemId] = useState(null);
  const [auditingParameterData, setAuditingParameterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableMetaData, setTableMetadata] = useState();
  const [auditingParameterDetails, setAuditingParameterDetails] = useState();

  const initialState = {
    title: "",
    legendId: "",
    status: true,
  };
  const [formData, setFormData] = useState(initialState);

  const auditingParameterActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.AuditingParametersAddAccess),
      canEdit: Boolean(permissionData?.AuditingParametersUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyAuditingParameterRowAction = useMemo(
    () => auditingParameterActionPermissions.canEdit,
    [auditingParameterActionPermissions]
  );

  const mapAuditingParameterRows = (rows = []) =>
    rows.map((item) => ({
      ...item,
      // Metadata expects `parameterTitle`, filter response returns `title`.
      parameterTitle: item?.title || "",
      updatedOn: moment(item?.updatedOn).format("MM-DD-YYYY"),
      status: item?.status ? "active" : "deactive",
    }));

  const handleEdit = (row) => {
    // console.log('...row', row)
    setAuditingParameterDetails(row);
    setItemId(row?.id);
    setAuditingParameterAddEditModalOpen(true);
  };

  useEffect(() => {
    fetchAuditingParameterContextData();
    fetchAuditingParameterList();
  }, []);

  const fetchAuditingParameterList = async () => {
    setLoading(true);
    const response = await submitAuditingParameterFilterData({});
    const valueReModified = mapAuditingParameterRows(response?.data || []);
    if (response) {
      setAuditingParameterData(valueReModified);
    } else {
      setAuditingParameterData([]);
    }
    setLoading(false);
  };

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return auditingParameterData;

    return auditingParameterData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [auditingParameterData]);

  const handleSearchSubmit = async (value) => {
    setLoading(true);
    const payload = { name: value };
    const response = await submitAuditingParameterFilterData(payload);
    const searchData = mapAuditingParameterRows(response?.data || []);

    if (response) {
      setAuditingParameterData(searchData);
    } else {
      setAuditingParameterData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const valueReModified = auditingParameterContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
    }));
    setTableMetadata(valueReModified);
  }, [auditingParameterContextData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Auditing Parameter</h5>

                {auditingParameterActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setAuditingParameterAddEditModalOpen(true);
                        setItemId(null);
                        setFormData(initialState);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && auditingParameterData?.length && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isEditAccess={auditingParameterActionPermissions.canEdit}
                  onEdit={
                    auditingParameterActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isPagination={false}
                  isActionRequired={hasAnyAuditingParameterRowAction}
                  isCheckboxLeftSide={false}
                  pageName={"Auditing Parameter"}
                />
              ) : (
                // <Spinners setLoading={setLoading} />
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AuditingParameterAddEditModal
            setAuditingParameterAddEditModalOpen={setAuditingParameterAddEditModalOpen}
            auditingParameterAddEditModalOpen={auditingParameterAddEditModalOpen}
            setFormData={setFormData}
            formData={formData}
            itemId={itemId}
            getAuditingParameterList={fetchAuditingParameterList}
            auditingParameterDetails={auditingParameterDetails}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AuditingParameter.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AuditingParameter);
